import React, { useMemo, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import SelectClear from '@atlaskit/icon/core/migration/cross-circle--select-clear';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import Tooltip from '@atlaskit/tooltip';

import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ExpandCollapseToggle } from '@atlassian/jira-servicedesk-cmdb-expand-collapse-toggle/src/ui/index.tsx';
import { AttributesList } from '@atlassian/jira-servicedesk-insight-attribute-list/src/ui/index.tsx';
import { useInsightVisibility } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { useSpecificObjectSchemaPermissionCheck } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/services/permissions/index.tsx';
import { MemoizedInsightIcon } from '@atlassian/jira-servicedesk-insight-icon/src/ui/index.tsx';
import type { CmdbReferencedObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute-values.tsx';
import type {
	CmdbObject,
	CombinedWorkspaceObjectId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import messages from './messages.tsx';
import ObjectDetailsIcon from './object-details-icon/index.tsx';

export type Props = {
	isExpanded: boolean;
	isEditing: boolean;
	object: CmdbObject;
	attributesToDisplay: string[];
	onDelete: (arg1: CmdbObject, arg2: UIAnalyticsEvent) => void;
	onExpandCollapseClick: (arg1: UIAnalyticsEvent, arg2: CombinedWorkspaceObjectId) => void;
	onViewObjectDetails: (arg1: CmdbObject, arg2: UIAnalyticsEvent) => void;
};

export const CmdbObjectCard = (props: Props) => {
	const {
		object: {
			avatar: { mediaClientConfig, url48 },
			label,
			attributes,
			workspaceId,
			objectType: { objectSchemaId },
		},
		attributesToDisplay,
	} = props;
	const { formatMessage } = useIntl();
	const isVisible = useInsightVisibility();

	const { data: hasObjectSchemaPermissions } = useSpecificObjectSchemaPermissionCheck(
		workspaceId,
		objectSchemaId,
	);

	const displayedAttributes = useMemo(
		() =>
			attributesToDisplay
				.map((attrName) => attributes.find((attr) => attr.objectTypeAttribute.name === attrName))
				.filter(Boolean),
		[attributes, attributesToDisplay],
	);

	const handleDelete = (event: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		props.onDelete(props.object, analyticsEvent);
	};

	const handleExpandCollapseClick = (analyticsEvent: UIAnalyticsEvent) => {
		props.onExpandCollapseClick(analyticsEvent, props.object.id);
	};

	const handleViewObjectDetails = (
		event: SyntheticEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		props.onViewObjectDetails(props.object, analyticsEvent);
	};

	const handleObjectAttributeClick = (_objectReference: CmdbReferencedObject) => {
		// TODO JDW-2521 Open object dialog and fetch required data
	};

	const renderDeleteButton = () => {
		const buttonLabel = formatMessage(messages.deleteObject);
		return (
			<Tooltip content={buttonLabel}>
				<DeleteButtonWrapper>
					<Button
						appearance="subtle"
						onClick={handleDelete}
						iconBefore={<SelectClear spacing="spacious" label={buttonLabel} />}
						testId="issue-field-cmdb-object.ui.card.button-delete"
						interactionName="auto-generated.issue-field-cmdb-object.ui.card.button-delete.button"
					/>
				</DeleteButtonWrapper>
			</Tooltip>
		);
	};

	const renderViewDetailsButton = () => {
		if (!isVisible) return null;

		if (hasObjectSchemaPermissions === true) {
			const buttonLabel = formatMessage(messages.viewFullObjectDetails);
			return (
				<Tooltip content={buttonLabel}>
					<Button
						onClick={handleViewObjectDetails}
						appearance="subtle"
						iconBefore={<ObjectDetailsIcon label={buttonLabel} />}
						testId="issue-field-cmdb-object.ui.card.button-view-details"
						interactionName="auto-generated.issue-field-cmdb-object.ui.card.button-view-details.button1"
					/>
				</Tooltip>
			);
		}

		const permissionsLabel = formatMessage(
			hasObjectSchemaPermissions === false
				? messages.noSchemaPermission
				: messages.loadingPermissions,
		);
		return (
			<Tooltip content={permissionsLabel}>
				<ButtonSizeAndShape>
					<ObjectDetailsIcon label={permissionsLabel} />
				</ButtonSizeAndShape>
			</Tooltip>
		);
	};

	const renderChevronButton = () => {
		if (displayedAttributes.length === 0) {
			return null;
		}
		const buttonLabel = formatMessage(
			props.isExpanded ? messages.showLessDetails : messages.showDetails,
		);
		return (
			<ExpandCollapseToggle
				isExpanded={props.isExpanded}
				label={buttonLabel}
				onClick={handleExpandCollapseClick}
			/>
		);
	};

	return (
		// eslint-disable-next-line jira/integration/test-id-by-folder-structure
		<Container data-testid="issue-field-cmdb-object.ui.cmdb-object-card">
			<Header>
				<MemoizedInsightIcon
					mediaClientConfig={mediaClientConfig}
					iconUrl={url48}
					size="small"
					label={label}
				/>
				<Name>{label}</Name>
				<CardActions>
					{props.isEditing ? renderDeleteButton() : renderViewDetailsButton()}
					{renderChevronButton()}
				</CardActions>
			</Header>
			{props.isExpanded && (
				<Box xcss={attributesListContainerStyles}>
					<AttributesList
						attributes={displayedAttributes}
						objectId={props.object.id}
						onObjectAttributeClick={handleObjectAttributeClick}
					/>
				</Box>
			)}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	position: 'relative',
	width: '100%',

	boxShadow: token('elevation.shadow.raised'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	marginBottom: token('space.100'),
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Name = styled.div({
	font: token('font.heading.xsmall'),

	color: token('color.text.subtle'),
	margin: 0,
	paddingLeft: token('space.100'),
	flexGrow: 1,
	minWidth: 0,
	overflowWrap: 'break-word',
});

const attributesListContainerStyles = xcss({
	marginTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardActions = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginTop: token('space.negative.050'),
	marginBottom: token('space.negative.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> *': {
		marginLeft: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 4}px`,
		margin: 0,
		padding: 0,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'row',
	/* Unfortunately, need due to some wrapping component in avatar */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > div:nth-of-type(1)': {
		marginTop: token('space.negative.025'),
		marginBottom: token('space.negative.025'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeleteButtonWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'button:hover': {
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			color: token('color.icon.danger'),
		},
	},
});

/**
 * Wrapper component to simulate the size and shape of an @atlaskit/button
 * containing an icon. We use this instead of using a button with isDisabled as
 * we cannot attach an @atlaskit/tooltip to disabled buttons as they do not emit
 * standard events.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonSizeAndShape = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 4}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 4}px`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = {
	Header,
	DeleteButtonWrapper,
	ButtonSizeAndShape,
};
